import { ColumnsType } from "antd/es/table";
import { TFunctionType } from "src/static/constants";
import { IObjectItem, IObjectCategory, IObjectTotal } from "src/app/services/sheets/type";

export const MONTH_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

export const getMonthLabel = (key: string, t: TFunctionType) => {
  const map: Record<string, string> = {
    january: t("months.jan"),
    february: t("months.feb"),
    march: t("months.march"),
    april: t("months.apr"),
    may: t("months.may"),
    june: t("months.jun"),
    july: t("months.jul"),
    august: t("months.aug"),
    september: t("months.sep"),
    october: t("months.oct"),
    november: t("months.nov"),
    december: t("months.dec"),
  };
  return map[key] || key;
};

export interface IFlatRow {
  key: string;
  rowType: "forecast" | "actual" | "difference";
  no?: number;
  name?: string;
  contract_amount?: number | null;
  current_month_amount?: number | null;
  total_kw?: number | null;
  installed_kw?: number | null;
  installed_percent?: number | null;
  materials: number | null;
  installation: number | null;
  logistics: number | null;
  other_production: number | null;
  office_expenses: number | null;
  total_cost: number | null;
  cost_per_kw: number | null;
  profit_with_office: number | null;
  profit_without_office: number | null;
  isTotal?: boolean;
}

export const flattenObjectData = (
  category: IObjectCategory | undefined,
  t: TFunctionType
): IFlatRow[] => {
  if (!category) return [];
  const rows: IFlatRow[] = [];

  const addObjectRows = (
    obj: IObjectItem | IObjectTotal,
    index: number,
    isTotal = false
  ) => {
    const base = {
      no: isTotal ? undefined : (obj as IObjectItem).no,
      name: isTotal ? t("sidebar.analyses.total") : (obj as IObjectItem).name,
      contract_amount: obj.contract_amount,
      current_month_amount: obj.current_month_amount,
      total_kw: obj.total_kw,
      installed_kw: obj.installed_kw,
      installed_percent: obj.installed_percent,
      isTotal,
    };

    rows.push({
      ...base,
      key: `${index}_forecast`,
      rowType: "forecast",
      ...obj.forecast,
    });
    rows.push({
      ...base,
      key: `${index}_actual`,
      rowType: "actual",
      ...obj.actual,
    });
    rows.push({
      ...base,
      key: `${index}_difference`,
      rowType: "difference",
      ...obj.difference,
    });
  };

  category.objects.forEach((obj, i) => addObjectRows(obj, i));
  if (category.total) {
    addObjectRows(category.total as any, category.objects.length, true);
  }

  return rows;
};

const formatNum = (val: number | null | undefined) => {
  if (val === null || val === undefined) return "-";
  const abs = Math.abs(Math.round(val));
  const formatted = abs.toLocaleString("en-US");
  if (val < 0) return `(${formatted})`;
  return formatted;
};

const formatPercent = (val: number | null | undefined) => {
  if (val === null || val === undefined) return "-";
  return `${Number(val).toFixed(1)}%`;
};

const mergedCell = (rowType: string) => {
  if (rowType === "forecast") return { rowSpan: 3 };
  return { rowSpan: 0 };
};

export const objectColumns = (t: TFunctionType): ColumnsType<IFlatRow> => [
  {
    title: "№",
    dataIndex: "no",
    key: "no",
    width: 45,
    fixed: "left",
    onCell: (record) => mergedCell(record.rowType),
    render: (val) => val ?? "",
  },
  {
    title: t("sidebar.analyses.objectName"),
    dataIndex: "name",
    key: "name",
    width: 220,
    fixed: "left",
    onCell: (record) => mergedCell(record.rowType),
  },
  {
    title: t("sidebar.analyses.summa"),
    children: [
      {
        title: t("sidebar.analyses.contractAmount"),
        dataIndex: "contract_amount",
        key: "contract_amount",
        width: 140,
        onCell: (record: IFlatRow) => mergedCell(record.rowType),
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.currentMonthAmount"),
        dataIndex: "current_month_amount",
        key: "current_month_amount",
        width: 140,
        onCell: (record: IFlatRow) => mergedCell(record.rowType),
        render: (val: number | null) => formatNum(val),
      },
    ],
  },
  {
    title: t("sidebar.analyses.totalKw"),
    dataIndex: "total_kw",
    key: "total_kw",
    width: 90,
    onCell: (record) => mergedCell(record.rowType),
    render: (val) => (val !== null && val !== undefined ? val : "-"),
  },
  {
    title: t("sidebar.analyses.installedGroup"),
    children: [
      {
        title: "Kw",
        dataIndex: "installed_kw",
        key: "installed_kw",
        width: 80,
        onCell: (record: IFlatRow) => mergedCell(record.rowType),
        render: (val: number | null) =>
          val !== null && val !== undefined ? val : "-",
      },
      {
        title: t("sidebar.analyses.percent"),
        dataIndex: "installed_percent",
        key: "installed_percent",
        width: 70,
        onCell: (record: IFlatRow) => mergedCell(record.rowType),
        render: (val: number | null) => formatPercent(val),
      },
    ],
  },
  {
    title: "",
    dataIndex: "rowType",
    key: "rowType",
    width: 80,
    render: (val: string) => {
      if (val === "forecast") return t("sidebar.analyses.forecast");
      if (val === "actual") return t("sidebar.analyses.actual");
      return t("sidebar.analyses.difference");
    },
  },
  {
    title: t("sidebar.analyses.costStructure"),
    children: [
      {
        title: t("sidebar.analyses.materials"),
        dataIndex: "materials",
        key: "materials",
        width: 140,
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.installation"),
        dataIndex: "installation",
        key: "installation",
        width: 130,
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.logistics"),
        dataIndex: "logistics",
        key: "logistics",
        width: 130,
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.otherProduction"),
        dataIndex: "other_production",
        key: "other_production",
        width: 140,
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.officeExpenses"),
        dataIndex: "office_expenses",
        key: "office_expenses",
        width: 140,
        render: (val: number | null) => formatNum(val),
      },
    ],
  },
  {
    title: t("sidebar.analyses.totalCost"),
    dataIndex: "total_cost",
    key: "total_cost",
    width: 150,
    render: (val) => <strong>{formatNum(val)}</strong>,
  },
  {
    title: t("sidebar.analyses.costPerKw"),
    dataIndex: "cost_per_kw",
    key: "cost_per_kw",
    width: 120,
    render: (val) => formatNum(val),
  },
  {
    title: t("sidebar.analyses.profitOrLoss"),
    children: [
      {
        title: t("sidebar.analyses.profitWithOffice"),
        dataIndex: "profit_with_office",
        key: "profit_with_office",
        width: 160,
        render: (val: number | null) => formatNum(val),
      },
      {
        title: t("sidebar.analyses.profitWithoutOffice"),
        dataIndex: "profit_without_office",
        key: "profit_without_office",
        width: 160,
        render: (val: number | null) => formatNum(val),
      },
    ],
  },
];
